import styled from "styled-components";
import { black, gray, grayDark, white } from "../styles/colors";
import { APP_STORE_URL } from "../constants/links";
import { PRICING_PLANS } from "../constants/pricing";
import { ScrollReveal } from "./ScrollReveal";

export function Pricing() {
  return (
    <Section id="pricing">
      <Header>
        <ScrollReveal>
          <Eyebrow>Pricing</Eyebrow>
          <Title>Start free. Go Pro when you outgrow it.</Title>
          <Subtitle>
            Free is enough to prove the habit. Pro removes the caps when your
            timeline becomes something you never want to lose.
          </Subtitle>
        </ScrollReveal>
      </Header>
      <Grid>
        {PRICING_PLANS.map((plan, index) => (
          <ScrollReveal key={plan.id} delay={index as 0 | 1}>
            <Plan $highlighted={Boolean(plan.highlighted)}>
              <PlanName>{plan.name}</PlanName>
              <PriceRow>
                <Price>{plan.price}</Price>
                <Period>{plan.period}</Period>
              </PriceRow>
              <PlanDescription>{plan.description}</PlanDescription>
              <FeatureList>
                {plan.features.map((feature) => (
                  <FeatureItem key={feature}>{feature}</FeatureItem>
                ))}
              </FeatureList>
              <PlanCta
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener"
                $highlighted={Boolean(plan.highlighted)}
              >
                {plan.id === "pro" ? "Upgrade in the app" : "Download free"}
              </PlanCta>
            </Plan>
          </ScrollReveal>
        ))}
      </Grid>
      <FinePrint>
        Pro is billed through Stripe in the Progress app. Text steps are always
        unlimited on Free.
      </FinePrint>
    </Section>
  );
}

const Section = styled.section({
  padding: "96px 24px",
  background: "#F7F6F4",
});

const Header = styled.div({
  maxWidth: 680,
  margin: "0 auto 48px",
  textAlign: "center",
});

const Eyebrow = styled.p({
  margin: "0 0 12px",
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: gray,
});

const Title = styled.h2({
  margin: "0 0 16px",
  fontSize: "clamp(32px, 4vw, 44px)",
  fontWeight: 700,
  letterSpacing: "-0.03em",
  color: black,
});

const Subtitle = styled.p({
  margin: 0,
  fontSize: 18,
  lineHeight: 1.7,
  color: grayDark,
});

const Grid = styled.div({
  maxWidth: 880,
  margin: "0 auto",
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: 20,
  alignItems: "stretch",

  "@media (max-width: 720px)": {
    gridTemplateColumns: "1fr",
  },
});

const Plan = styled.div<{ $highlighted: boolean }>(({ $highlighted }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  padding: "32px 28px 28px",
  borderRadius: 20,
  border: $highlighted
    ? "1px solid rgba(35, 35, 35, 0.18)"
    : "1px solid rgba(35, 35, 35, 0.08)",
  background: $highlighted ? black : white,
  color: $highlighted ? white : black,
  boxShadow: $highlighted ? "0 20px 48px rgba(35, 35, 35, 0.18)" : "none",
}));

const PlanName = styled.h3({
  margin: "0 0 12px",
  fontSize: 18,
  fontWeight: 700,
  letterSpacing: "-0.01em",
});

const PriceRow = styled.div({
  display: "flex",
  alignItems: "baseline",
  gap: 6,
  marginBottom: 12,
});

const Price = styled.span({
  fontSize: 48,
  fontWeight: 700,
  letterSpacing: "-0.04em",
  lineHeight: 1,
});

const Period = styled.span({
  fontSize: 15,
  fontWeight: 600,
  opacity: 0.64,
});

const PlanDescription = styled.p({
  margin: "0 0 24px",
  fontSize: 15,
  lineHeight: 1.65,
  opacity: 0.78,
});

const FeatureList = styled.ul({
  margin: "0 0 28px",
  padding: 0,
  listStyle: "none",
  display: "flex",
  flexDirection: "column",
  gap: 12,
  flex: 1,
});

const FeatureItem = styled.li({
  position: "relative",
  paddingLeft: 22,
  fontSize: 15,
  lineHeight: 1.5,

  "&::before": {
    content: '"✓"',
    position: "absolute",
    left: 0,
    top: 0,
    fontWeight: 700,
    opacity: 0.55,
  },
});

const PlanCta = styled.a<{ $highlighted: boolean }>(({ $highlighted }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  height: 48,
  padding: "0 20px",
  borderRadius: 12,
  fontSize: 15,
  fontWeight: 700,
  letterSpacing: "-0.01em",
  background: $highlighted ? white : black,
  color: $highlighted ? black : white,
  transition: "transform 0.2s ease, opacity 0.2s ease",

  "&:hover": {
    transform: "translateY(-1px)",
    opacity: 0.92,
  },
}));

const FinePrint = styled.p({
  maxWidth: 560,
  margin: "28px auto 0",
  textAlign: "center",
  fontSize: 13,
  lineHeight: 1.6,
  color: grayDark,
});
